// Relay's four sounds, made live with Web Audio: no audio files, nothing to license.
// Chosen on 4 October 2026: Knock knock, Swoosh, Coin drop, Soft fall.
// Quiet by design, never during a transfer, and off with one switch in Keys.
// On iPhone they follow the silent switch. On Android they follow media volume.

let ctx = null, master = null, verbIn = null, enabled = true;
const BUZZ = { request: [30, 70, 30], sent: [20], arrived: [15, 40, 25], failed: [70] };
const VOLUME = 0.32; // the "60%" level from the sound board

export function setEnabled(on) { enabled = !!on; }

// Browsers only allow sound after the person has tapped something, so the first tap
// anywhere gets the audio ready. Later taps wake it again if the phone paused it.
export function unlock() {
  if (!enabled) return;
  try {
    if (!ctx) {
      // "ambient" = respect the iPhone silent switch and don't stop the person's music.
      try { if (navigator.audioSession) navigator.audioSession.type = 'ambient'; } catch {}
      const AC = window.AudioContext || window.webkitAudioContext;
      if (!AC) return;
      ctx = new AC();
      master = ctx.createGain(); master.gain.value = VOLUME;
      const comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -16; comp.ratio.value = 4; comp.attack.value = 0.003; comp.release.value = 0.2;
      master.connect(comp); comp.connect(ctx.destination);
      // a small, soft room so notes ring a little instead of stopping dead
      const verb = ctx.createConvolver(), len = Math.floor(ctx.sampleRate * 1.3), ir = ctx.createBuffer(2, len, ctx.sampleRate);
      for (let ch = 0; ch < 2; ch++) { const d = ir.getChannelData(ch); for (let i = 0; i < len; i++) d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, 3.4); }
      verb.buffer = ir; verbIn = ctx.createGain(); verbIn.gain.value = 0.9; verbIn.connect(verb); verb.connect(master);
    }
    if (ctx.state !== 'running') ctx.resume().catch(() => {});
  } catch {}
}

export function play(name) {
  if (!enabled || !SOUNDS[name]) return;
  try { globalThis.__relaySoundLog?.push(name + (ctx?.state === 'running' ? '' : ' (not ready)')); } catch {} // lets the tests check which sound played
  try { if (navigator.vibrate && /Android/i.test(navigator.userAgent)) navigator.vibrate(BUZZ[name]); } catch {}
  if (!ctx) return; // no tap yet, so the browser wouldn't let us play anyway
  const go = () => { try { SOUNDS[name](ctx.currentTime + 0.03); } catch {} };
  if (ctx.state === 'running') go();
  else ctx.resume().then(() => { if (ctx.state === 'running') go(); }, () => {});
}

// ---------------------------------------------------------------- building blocks
function out(wet) { const g = ctx.createGain(); g.connect(master); if (wet) { const s = ctx.createGain(); s.gain.value = wet; g.connect(s); s.connect(verbIn); } return g; }
function env(g, t, peak, a, d) { g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(Math.max(peak, 0.0002), t + a); g.gain.exponentialRampToValueAtTime(0.0001, t + a + d); }
// partials: [frequency ratio, loudness, how long it rings compared with the base note]
const GLASS = [[1, 1, 1], [2, 0.28, 0.55], [3, 0.08, 0.35]];
const METAL = [[1, 1, 1], [2.76, 0.42, 0.55], [5.4, 0.2, 0.35], [8.93, 0.08, 0.2]];
function bell(t, f, { dur = 0.8, gain = 0.25, partials = GLASS, wet = 0.25 } = {}) {
  const o = out(wet);
  for (const [r, amp, dm] of partials) {
    if (f * r > ctx.sampleRate * 0.45) continue;
    const osc = ctx.createOscillator(), g = ctx.createGain();
    osc.frequency.value = f * r; env(g, t, gain * amp, 0.004, dur * dm);
    osc.connect(g); g.connect(o); osc.start(t); osc.stop(t + dur * dm + 0.05);
  }
}
function tone(t, f1, f2, { dur = 0.3, gain = 0.25, type = 'sine', lp = 0, wet = 0.15, at = 0.02, over = dur * 0.8 } = {}) {
  const o = out(wet), osc = ctx.createOscillator(), g = ctx.createGain();
  osc.type = type; osc.frequency.setValueAtTime(f1, t); osc.frequency.exponentialRampToValueAtTime(f2, t + over);
  env(g, t, gain, at, dur);
  let node = osc; if (lp) { const f = ctx.createBiquadFilter(); f.type = 'lowpass'; f.frequency.value = lp; osc.connect(f); node = f; }
  node.connect(g); g.connect(o); osc.start(t); osc.stop(t + at + dur + 0.05);
}
function swoosh(t, { dur = 0.42, from = 400, to = 4200, q = 1.3, gain = 0.45, wet = 0.2 } = {}) {
  const o = out(wet), n = ctx.createBufferSource(), b = ctx.createBuffer(1, Math.ceil(ctx.sampleRate * (dur + 0.05)), ctx.sampleRate), d = b.getChannelData(0);
  for (let i = 0; i < d.length; i++) d[i] = Math.random() * 2 - 1;
  n.buffer = b;
  const bp = ctx.createBiquadFilter(), g = ctx.createGain();
  bp.type = 'bandpass'; bp.Q.value = q; bp.frequency.setValueAtTime(from, t); bp.frequency.exponentialRampToValueAtTime(to, t + dur);
  g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(gain, t + dur * 0.55); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  n.connect(bp); bp.connect(g); g.connect(o); n.start(t); n.stop(t + dur + 0.05);
}

// ---------------------------------------------------------------- the four sounds
const SOUNDS = {
  // Someone wants to send you files: E5 then B5, bell.
  request: (t) => { bell(t, 659.25, { dur: 0.75, gain: 0.26 }); bell(t + 0.15, 987.77, { dur: 0.9, gain: 0.24 }); },
  // Your file was delivered: an air sweep up, with a faint rising tone under it.
  sent: (t) => { swoosh(t); tone(t + 0.02, 600, 1400, { dur: 0.36, gain: 0.06, over: 0.3 }); },
  // A file arrived: two metallic pings, B6 then E7, like a coin in a wallet.
  arrived: (t) => { bell(t, 1975.53, { dur: 0.45, gain: 0.16, partials: METAL, wet: 0.2 }); bell(t + 0.09, 2637.02, { dur: 0.9, gain: 0.17, partials: METAL, wet: 0.3 }); },
  // Declined or didn't go through: two soft falling notes, E4 then C4.
  failed: (t) => { tone(t, 329.63, 329.63, { dur: 0.25, gain: 0.22, type: 'triangle', lp: 1400, at: 0.01 }); tone(t + 0.17, 261.63, 261.63, { dur: 0.45, gain: 0.22, type: 'triangle', lp: 1200, at: 0.01 }); },
};
